import {Button} from 'antd';
import {FormInstance} from 'antd/es/form';
import cn from 'classnames';
import React, {useState, FC} from 'react';

import {IFormEntityData} from 'modules/data/data-types';
import {ReactComponent as Dislike} from 'shared/assets/forms/grade/dislike.svg';
import {ReactComponent as Like} from 'shared/assets/forms/grade/like.svg';
import {TOracleBoolType} from 'shared/types';
import {useAppDispatch} from 'store/config/hooks';
import {requestSliceActions} from 'store/slices/request-slice/request-slice';
import {toggleRating} from 'store/slices/request-slice/request-slice-api';
import './grade.less';

export type TGradeValue = number | TOracleBoolType;

interface IGradeProps {
    /**
     * Для метода нужно возвращать true при успешном завершении события
     * @param isLike
     */
    value?: TGradeValue;
    data?: IFormEntityData;
    form?: FormInstance;
    commentId?: string;
    requestFormKey?: string;
    url?: string;
}

enum EGradeStatus {
    LIKE = 'LIKE',
    DISLIKE = 'DISLIKE',
    NORMAL = 'NORMAL',
}

const resolveDefaultStatus = (status: number | string | TOracleBoolType = ''): EGradeStatus => {
    let result = EGradeStatus.NORMAL;

    const isLike = [1, 'Y'].includes(status);
    const isDislike = [0, 'N'].includes(status);

    if (isLike) {
        result = EGradeStatus.LIKE;
    } else if (isDislike) {
        result = EGradeStatus.DISLIKE;
    }

    return result;
};

export const Grade: FC<IGradeProps> = ({
    value,
    form,
    data,
    commentId,
    requestFormKey,
    url,
}: IGradeProps) => {
    const dispatch = useAppDispatch();
    const [status, setStatus] = useState<EGradeStatus>(resolveDefaultStatus(value));
    const {resetRequestStatusesData} = requestSliceActions;

    const isNormal = status === EGradeStatus.NORMAL;
    const showLike = isNormal || status === EGradeStatus.LIKE;
    const showDislike = isNormal || status === EGradeStatus.DISLIKE;
    const disabledLike = status === EGradeStatus.LIKE;
    const disabledDislike = status === EGradeStatus.DISLIKE;

    const handleChange = (isLike: boolean) => async () => {
        if (!isNormal || !commentId) return;

        const isSuccess = await (async () => {
            const key = 'rating';
            const keyForId = 'id';
            const newValue = +isLike;
            try {
                // todo get this as param
                await toggleRating({
                    commentId: data ? data[keyForId] : commentId,
                    rating: newValue,
                    url,
                });

                if (requestFormKey) {
                    dispatch(resetRequestStatusesData);
                }

                if (form) {
                    form.setFieldsValue({[key]: newValue});
                }
                return true;
            } catch (e) {
                console.error(e);
                return false;
            }
        })();

        if (isSuccess) {
            setStatus(isLike ? EGradeStatus.LIKE : EGradeStatus.DISLIKE);
        } else {
            setStatus(EGradeStatus.NORMAL);
        }
    };

    const buttonLike = (
        <Button
            className={cn('grade-button', {disabled: disabledLike})}
            icon={<Like />}
            onClick={handleChange(true)}
        />
    );

    const buttonDislike = (
        <Button
            className={cn('grade-button', {disabled: disabledDislike})}
            icon={<Dislike />}
            onClick={handleChange(false)}
        />
    );

    return (
        <div className="grade">
            {showLike && buttonLike}
            {showDislike && buttonDislike}
        </div>
    );
};
